import { FC, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import Text from '@/components/Text';
import useAnchoredTooltip from '@/hooks/useAnchoredTooltip';

export type BreakdownItem = { label: string; note?: string; value: string };

const TOOLTIP_WIDTH = 326;
const LABEL_WIDTH = 142;
const VALUE_WIDTH = 62;

const Trigger = styled.span`
  cursor: help;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.darkGray};
`;

const Box = styled.div<{ $left: number; $top: number; $width: number }>`
  position: fixed;
  z-index: 11;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: ${({ $width }) => $width}px;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  padding: 16px 16px 8px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(37, 40, 43, 0.16);
`;

const Title = styled(Text)`
  margin: 0;
  font-weight: 700;
  line-height: 16px;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
`;

const TotalRow = styled(Row)`
  padding: 12px 0 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const Label = styled(Text)`
  width: ${LABEL_WIDTH}px;
  word-break: break-word;
`;

const Amounts = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Note = styled(Text)`
  text-align: right;
  white-space: nowrap;
`;

const Value = styled(Text)`
  width: ${VALUE_WIDTH}px;
  text-align: right;
  white-space: nowrap;
`;

interface Props {
  title: string;
  items: BreakdownItem[];
  total?: string;
}

const BreakdownTooltip: FC<PropsWithChildren<Props>> = ({
  title,
  items,
  total,
  children,
}) => {
  const { triggerRef, tooltipRef, portalEl, position, triggerProps } =
    useAnchoredTooltip<HTMLSpanElement>(TOOLTIP_WIDTH);

  return (
    <Trigger ref={triggerRef} {...triggerProps}>
      {children}
      {createPortal(
        position ? (
          <Box
            ref={tooltipRef}
            $left={position.left}
            $top={position.top}
            $width={TOOLTIP_WIDTH}
          >
            <Title as="p" $type="small" $color="gray" $isUpperCase>
              {title}
            </Title>
            <Rows>
              {items.map((item, i) => (
                <Row key={i}>
                  <Label as="span" $type="smallBold">
                    {item.label}
                  </Label>
                  <Amounts>
                    {item.note && (
                      <Note as="span" $type="smallBold">
                        {item.note}
                      </Note>
                    )}
                    <Value as="span" $type="smallBold">
                      {item.value}
                    </Value>
                  </Amounts>
                </Row>
              ))}
            </Rows>
            {total && (
              <TotalRow>
                <Label as="span" $type="smallBold">
                  Total:
                </Label>
                <Amounts>
                  <Value as="span" $type="smallBold">
                    {total}
                  </Value>
                </Amounts>
              </TotalRow>
            )}
          </Box>
        ) : null,
        portalEl,
      )}
    </Trigger>
  );
};

export default BreakdownTooltip;
